import React from 'react';
import Cookies from 'js-cookie';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, Flex, Grid, Heading, Spinner, Text } from '@radix-ui/themes';

import Footer from 'Components/element/Footer';
import NavBar from 'Components/element/NavBar';
import { addBreadcrumbSchema } from 'Components/helpers';
import { useForEmployers } from './for-employers.provider';
import { PublicPlanCard } from './components/plan-card';
import './for-employers.scss';

const serviceCards = [
   {
      image: '/Images/forEmployers/forEmployers-card1.png',
      titleKeys: ['forEmployersPage.card1.header1.1'],
      descriptionKeys: [
         'forEmployersPage.card1.desc1',
         'forEmployersPage.card1.desc2',
         'forEmployersPage.card1.desc3',
         'forEmployersPage.card1.desc4',
         'forEmployersPage.card1.desc5',
      ],
   },
   {
      image: '/Images/forEmployers/forEmployers-card2.png',
      titleKeys: ['forEmployersPage.card2.header1.1'],
      descriptionKeys: [
         'forEmployersPage.card2.desc1',
         'forEmployersPage.card2.desc2',
         'forEmployersPage.card2.desc3',
         'forEmployersPage.card2.desc4',
         'forEmployersPage.card2.desc5',
         'forEmployersPage.card2.desc6',
         'forEmployersPage.card2.desc7',
         'forEmployersPage.card2.desc8',
      ],
   },
   {
      image: '/Images/forEmployers/forEmployers-card3.png',
      titleKeys: ['forEmployersPage.card3.header1.1', 'forEmployersPage.card3.header1.2'],
      descriptionKeys: [
         'forEmployersPage.card3.desc1',
         'forEmployersPage.card3.desc2',
         'forEmployersPage.card3.desc3',
         'forEmployersPage.card3.desc4',
         'forEmployersPage.card3.desc5',
         'forEmployersPage.card3.desc6',
         'forEmployersPage.card3.desc7',
      ],
   },
];

function ForEmployersView() {
   const { t } = useTranslation('global');
   const forEmployersStore = useForEmployers();

   const primaryColor = Cookies.get('primaryColor') ?? '#f3734c';
   const currency = Cookies.get('curr') ?? '';

   React.useEffect(() => {
      forEmployersStore.loadPlans();

      const metaTitle = document.querySelector('title');
      const metaDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]');

      if (metaTitle) {
         metaTitle.textContent = t('forEmployersPage.metaTitleValue');
      }

      if (metaDescription) {
         metaDescription.content = t('forEmployersPage.metaDescriptionValue');
      }

      window.scrollTo(0, 0);

      const breadcrumbs = [{ name: t('navHeaders.forEmployers') }];
      const script = addBreadcrumbSchema(breadcrumbs);

      return () => {
         if (script) document.head.removeChild(script);
      };
   }, [forEmployersStore, t]);

   return (
      <div className="forEmployers featureForEmployers">
         <NavBar />

         <div className="aboutusSection1 text-center">
            <h1>{t('forEmployersPage.pageTitle')}</h1>
            <h6 className="text-muted fw-normal">
               <Link to="/" style={{ color: 'grey' }}>
                  {t('navHeaders.home')}
               </Link>
               {' / '}
               {t('navHeaders.forEmployers')}
            </h6>
         </div>

         <div className="container forEmployersSection2">
            <div className="featureForEmployersRates">
               <Button
                  size="3"
                  radius="small"
                  type="button"
                  onClick={forEmployersStore.toggleRates}
                  style={{
                     width: 220,
                     backgroundColor: primaryColor,
                  }}
               >
                  {forEmployersStore.showRates
                     ? t('forEmployersPage.hideRates')
                     : t('forEmployersPage.showRates')}
               </Button>

               {forEmployersStore.showRates && (
                  <Box className="featureForEmployersRatesPanel">
                     {forEmployersStore.loading ? (
                        <Flex justify="center" py="6">
                           <Spinner size="2" />
                        </Flex>
                     ) : forEmployersStore.hasPlans ? (
                        <Grid columns={{ initial: '1', md: '2', xl: '3' }} gap="5">
                           {forEmployersStore.plans.map((plan) => (
                              <PublicPlanCard
                                 key={plan.plan_id}
                                 plan={plan}
                                 currency={currency}
                                 primaryColor={primaryColor}
                              />
                           ))}
                        </Grid>
                     ) : (
                        <Flex className="featureForEmployersEmpty" align="center" justify="center">
                           <Text color="gray">{t('pagination.NoofRecords')} 0</Text>
                        </Flex>
                     )}

                     <Box mt="6">
                        <Button asChild size="3" radius="small" variant="outline" style={{ width: 220 }}>
                           <Link to="/contact">{t('forEmployersPage.contacts')}</Link>
                        </Button>
                     </Box>
                  </Box>
               )}
            </div>

            <div className="row pb-5">
               <div className="col-lg-6 forEmloyersDesc">
                  <div className="row mb-3 mb-md-4 align-items-center">
                     <img
                        alt=""
                        className="forEmloyersIcon"
                        src="/Images/forEmployers/forEmployers-icon1.png"
                     />
                     <p className="col-8 fw-medium">{t('forEmployersPage.item1')}</p>
                  </div>
                  <div className="row mb-3 mb-md-4 align-items-center">
                     <img
                        alt=""
                        className="forEmloyersIcon"
                        src="/Images/forEmployers/forEmployers-icon2.png"
                     />
                     <p className="col-8 fw-medium">{t('forEmployersPage.item2')}</p>
                  </div>
                  <div className="row align-items-center">
                     <img
                        alt=""
                        className="forEmloyersIcon"
                        src="/Images/forEmployers/forEmployers-icon3.png"
                     />
                     <p className="col-9 fw-medium">{t('forEmployersPage.item3')}</p>
                  </div>
               </div>
               <div className="col-lg-6">
                  <img alt="" src="/Images/forEmployers/forEmployers-image.png" />
               </div>
            </div>

            <div className="aboutusDivider">
               <hr />
            </div>
         </div>

         <div className="container aboutusSection4 text-center">
            <h2 className="aboutusSec4Header mx-auto" style={{ margin: '5% 0 5% 0' }}>
               {t('forEmployersPage.header1.1')}
               <span className="textGradient">
                  {' '}
                  <span className="SubHaddingTxt">{t('forEmployersPage.header1.2')}</span>
               </span>
            </h2>

            <Grid className="featureForEmployersServicesGrid" columns={{ initial: '1', md: '3' }} gap="5">
               {serviceCards.map((card) => (
                  <Card key={card.image} className="featureForEmployersServiceCard" size="3">
                     <Flex direction="column" align="center" height="100%">
                        <img className="featureForEmployersServiceImage" src={card.image} alt="" />
                        <Heading as="h3" size="5" mt="4">
                           {card.titleKeys.map((key) => (
                              <React.Fragment key={key}>
                                 {t(key)}
                                 <br />
                              </React.Fragment>
                           ))}
                        </Heading>

                        <Box asChild className="featureForEmployersServiceList">
                           <ul>
                              {card.descriptionKeys.map((key) => (
                                 <li key={key}>{t(key)}</li>
                              ))}
                           </ul>
                        </Box>
                     </Flex>
                  </Card>
               ))}
            </Grid>

            <Flex mt="6" justify="between">
               <Link to="/candidate/resume/create" className="btn button1">
                  {t('aboutPage.postResume')}
               </Link>
               <Link to="/user/createjob" className="btn button1">
                  {t('aboutPage.postVacancy')}
               </Link>
            </Flex>
         </div>

         <Footer />
      </div>
   );
}

export const ForEmployers = observer(ForEmployersView);
